import {ChangeDetectorRef, Component, OnDestroy, OnInit} from '@angular/core';
import {DcpProductService} from '@app/product/services/dcp-product.service';
import {CategoryService} from '@app/services/category.service';
import {ArrayService, JsonService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Category, CmsComponent, ProductSearchPage} from '@spartacus/core';
import {CmsComponentData} from '@spartacus/storefront';
import {NavModel} from 'ng-design-system';
import {Observable, Subscription} from 'rxjs';
import {take} from 'rxjs/operators';

export interface PrioritizedPopularAppsComponent extends CmsComponent {
  uuid: string;
  container: string;
  headline: string;
  categoryPriorities?: string;
  categoryProductLists?: string;
}

export interface CategoryProductList {
  category: string;
  products: string[];
}

@UntilDestroy()
@Component({
  selector: 'dcp-popular-apps',
  templateUrl: './popular-apps.component.html',
  styleUrls: ['./popular-apps.component.scss',
    '../../shared/product-tile/product-tile.component.scss',
    '../../../styles/_tile.scss']
})
export class PopularAppsComponent implements OnInit, OnDestroy {

  productSearchPage: ProductSearchPage;
  categories: Category[];
  navItems: NavModel[];
  numberOfShownCategories: number = 4;
  numberOfShownProducts: number = 9;
  activeNavItem: NavModel;
  componentData$: Observable<PrioritizedPopularAppsComponent>;
  componentDataSubscription: Subscription = Subscription.EMPTY;
  categoryProductLists: CategoryProductList[];
  categoryPriorities: string[];
  totalResults: number = 0;

  constructor(
    private dcpProductService: DcpProductService,
    private cmsData: CmsComponentData<PrioritizedPopularAppsComponent>,
    private categoryService: CategoryService,
    private jsonService: JsonService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
  }

  ngOnInit() {
    this.componentData$ = this.cmsData.data$;
    this.fetchComponentData();
  }

  ngOnDestroy(): void {
    this.componentDataSubscription.unsubscribe();
  }

  fetchComponentData() {
    this.componentDataSubscription = this.componentData$.pipe(take(1), untilDestroyed(this)).subscribe((componentData) => {
      this.categoryProductLists = this.prepareCategoryData(componentData.categoryProductLists);
      this.categories = this.categoryService.getCategories().filter(category => !category.url);
      const navItemsLocal: NavModel[] = this.categoryService.categoriesToNavModels(this.categories);
      this.categoryPriorities = this.getCategoryPriorities(componentData, this.categoryProductLists);
      this.navItems = this.sortNavItems(this.categoryPriorities, navItemsLocal);
      this.activeNavItem = this.navItems[0];
      this.fetchProducts(this.activeNavItem.id, this.categoryProductLists);
    });
  }

  prepareCategoryData(rawJson: string): CategoryProductList[] {
    return this.jsonService.parseSafely(rawJson) as CategoryProductList[];
  }

  /**
   * Gets the products and handles their subscriptions.
   *
   * @param categoryId id of the category the products should be filtered by.
   * @param categoryProductLists Products to be shown for a category configured in SmartEdit.
   */
  fetchProducts(categoryId: string, categoryProductLists: CategoryProductList[]) {
    if (categoryProductLists) {
      const productCodes: string[] = this.getProductCodes(categoryId, categoryProductLists);
      this.fetchPopularProductsByProductCodes(productCodes);
      this.fetchTotalResultsForCategory(categoryId);
    }
  }

  onNavChangeById(id: string) {
    this.fetchProducts(id, this.categoryProductLists);
    this.activeNavItem = this.navItems.find(n => n.id === id);
  }

  /**
   * Redirects the user to a search page with all products of a category.
   *
   * @param categoryId to show the products of
   */
  displayProductsOfCategory(categoryId: string) {
    this.categoryService.displayProductsOfCategory(categoryId);
  }

  /**
   * Sorts the navItems by the component data of the PrioritizedPopularAppsComponent.
   *
   * @param categoryPriorities data of the component.
   * @param navItems Items to sort.
   */
  sortNavItems(categoryPriorities: string[], navItems: NavModel[]) {
    try {
      navItems = ArrayService.sortObjectsArrayByStringArray(
        navItems, categoryPriorities, 'id'
      );
    } catch (e) {
    }
    return navItems;
  }

  /**
   * Creates a list of product codes
   *
   * @param categoryId id of the category the products should be filtered by.
   * @param categoryProductLists Products to be shown for a category configured in SmartEdit.
   */
  private getProductCodes(categoryId: string, categoryProductLists: CategoryProductList[]): string[] {
    let productCodes: string[] = [];
    const productList: CategoryProductList = categoryProductLists.find(
      (categoryProductList) => categoryProductList.category === categoryId
    );
    if (productList) {
      productCodes = productList.products;
    }
    return productCodes;
  }

  private fetchPopularProductsByProductCodes(productCodes: string[]) {
    this.dcpProductService.fetchPopularProductsByProductCodes(productCodes)
      .pipe(take(1), untilDestroyed(this))
      .subscribe(value => {
        this.productSearchPage = value;
        if (this.productSearchPage && this.productSearchPage.products && this.productSearchPage.products.length) {
          this.productSearchPage.products =
            ArrayService.sortObjectsArrayByStringArray(this.productSearchPage.products, productCodes, 'code');
        }
        this.changeDetectorRef.detectChanges();
      });
  }

  private fetchTotalResultsForCategory(categoryId: string) {
    this.dcpProductService.fetchTotalResultsForCategory(categoryId).pipe(take(1), untilDestroyed(this)).subscribe(value => {
      this.totalResults = 0;
      if (value && value.pagination) {
        this.totalResults = value.pagination.totalResults;
        this.changeDetectorRef.detectChanges();
      }
    });
  }

  private getCategoryPriorities(componentData: PrioritizedPopularAppsComponent, categoryProductLists: CategoryProductList[]): string[] {
    if (this.categoryProductLists) {
      return categoryProductLists.map(productList => productList.category);
    } else {
      return this.jsonService.parseSafely(componentData.categoryPriorities) as string[];
    }
  }
}
